// PATH: src/pages/ProximosEventos.jsx
// VERSION: 4.4 — Eventos especiales sin sección recurrentes (fixed_events)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Image,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Button,
  Flex,
  Badge,
  Container,
  HStack,
  Input,
  Checkbox,
  Spinner,

  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@chakra-ui/icons';
import { FiClock, FiMapPin } from 'react-icons/fi';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { cld } from '../utils/cloudinary';
import { API } from '../utils/apiBase';

const PAGE_SIZE = 6;
const HIGHLIGHT_MS = 3000;

async function fetchJson(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function normalizeEventsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.events)) return payload.events;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}
function normalizeAlbumsPayload(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.albums)) return payload.albums;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

const normalizeDate = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const buildEventKey = (e) => {
  const raw = e?.key || `${e?.date || ''}|${e?.title || ''}`;
  return String(raw).trim();
};

export default function ProximosEventos() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('todos');
  const [sortAsc, setSortAsc] = useState(true);

  const [events, setEvents] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [pendingFocusKey, setPendingFocusKey] = useState(
    () => searchParams.get('focusEventKey') || searchParams.get('eventKey'),
  );
  const [highlightedKey, setHighlightedKey] = useState(null);

  const searchPlaceholder = useBreakpointValue({
    base: 'Buscar…',
    md: 'Buscar por título, lugar o descripción…',
  });
  const checkboxColor = useColorModeValue("black", "white");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const [ev, al] = await Promise.all([
          fetchJson(`${API}/events?limit=1000`),
          fetchJson(`${API}/albums?limit=1000`),
        ]);
        if (!alive) return;
        setEvents(normalizeEventsPayload(ev));
        setAlbums(normalizeAlbumsPayload(al));
      } catch (e) {
        if (alive) setErr(e?.message || 'Error al cargar');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const items = Array.isArray(events) ? events : [];

  const albumIdByEventKey = useMemo(() => {
    const map = new Map();
    (albums || []).forEach((a) => {
      if (a?.eventKey && a?.id && !map.has(String(a.eventKey).trim())) {
        map.set(String(a.eventKey).trim(), String(a.id).trim());
      }
    });
    return map;
  }, [albums]);

  const isPast = (d) => normalizeDate(d) < today;
  const isUpcoming = (d) => !isPast(d);
  const isToday = (d) =>
    normalizeDate(d).getTime() === today.getTime();

  const byQuery = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (e) =>
        (e.title ?? '').toLowerCase().includes(q) ||
        (e.description ?? '').toLowerCase().includes(q) ||
        (e.place ?? '').toLowerCase().includes(q) ||
        (e.time ?? '').toLowerCase().includes(q),
    );
  }, [items, query]);

  const filtered = useMemo(() => {
    if (status === 'activos') return byQuery.filter((e) => isUpcoming(e.date));
    if (status === 'vencidos') return byQuery.filter((e) => isPast(e.date));
    return byQuery;
  }, [byQuery, status]);

  const finalSorted = useMemo(() => {
    const cmp = (a, b) =>
      sortAsc
        ? new Date(a.date) - new Date(b.date)
        : new Date(b.date) - new Date(a.date);
    if (status === 'todos') {
      const upcoming = filtered
        .filter((e) => isUpcoming(e.date))
        .sort(cmp);
      const past = filtered.filter((e) => isPast(e.date)).sort(cmp);
      return [...upcoming, ...past];
    }
    return [...filtered].sort(cmp);
  }, [filtered, status, sortAsc]);

  const totalPages = Math.ceil(finalSorted.length / PAGE_SIZE) || 1;
  const start = (currentPage - 1) * PAGE_SIZE;
  const pageEvents = finalSorted.slice(start, start + PAGE_SIZE);

  useEffect(() => {
    if (!pendingFocusKey) return;
    setStatus('todos');
    setQuery('');
    setSortAsc(true);
  }, [pendingFocusKey]);

  useEffect(() => {
    if (!pendingFocusKey) return;
    const idx = finalSorted.findIndex(
      (e) => buildEventKey(e) === pendingFocusKey,
    );
    if (idx >= 0) {
      const targetPage = Math.floor(idx / PAGE_SIZE) + 1;
      if (targetPage !== currentPage) setCurrentPage(targetPage);
    }
  }, [pendingFocusKey, finalSorted, currentPage]);

  const cardRefs = useRef(new Map());
  const setCardRef =
    (key) =>
      (el) => {
        if (el) cardRefs.current.set(key, el);
      };

  useEffect(() => {
    if (!pendingFocusKey) return;
    const el = cardRefs.current.get(pendingFocusKey);
    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedKey(pendingFocusKey);

    const params = new URLSearchParams(searchParams);
    params.delete('focusEventKey');
    params.delete('eventKey');
    setSearchParams(params, { replace: true });

    setPendingFocusKey(null);
  }, [pageEvents, pendingFocusKey, searchParams, setSearchParams]);

  useEffect(() => {
    if (!highlightedKey) return;
    const t = setTimeout(() => setHighlightedKey(null), HIGHLIGHT_MS);
    return () => clearTimeout(t);
  }, [highlightedKey]);

  const onChangeQuery = (e) => {
    setQuery(e.target.value);
    setCurrentPage(1);
  };
  const onCheckStatus = (value) => {
    setStatus(value);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <Box bg="bg.default" flex="1">
        <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
          <HStack justify="center" py={10}>
            <Spinner size="lg" />
            <Text> Cargando eventos…</Text>
          </HStack>
        </Container>
      </Box>
    );
  }

  if (err) {
    return (
      <Box bg="bg.default" flex="1">
        <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
          <Heading size="md" color="red.600">
            Error al cargar eventos
          </Heading>
          <Text color="gray.700" mt={2}>
            {String(err)}
          </Text>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="bg.default" flex="1">
      <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
        <Heading mb={8} textAlign="center" color="text.default">
          Eventos Especiales
        </Heading>

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={3}
          mb={6}
          align={{ base: 'stretch', md: 'center' }}
          justify="space-between"
        >
          <Input
            borderColor="gray"
            borderWidth="2px"
            placeholder={searchPlaceholder}
            value={query}
            onChange={onChangeQuery}
            bg="bg.card"
            _placeholder={{ color: 'text.muted' }}
            _hover={{ borderColor: 'blue' }}
            maxW={{ base: '100%', md: '340px' }}
          />

          <HStack
            spacing={3}
            align="center"
            justify={{ base: 'flex-start', md: 'flex-end' }}
            flexWrap="wrap"
          >
            <HStack spacing={2}>
              <Checkbox
                size="sm"
                isChecked={status === 'activos'}
                onChange={() => onCheckStatus('activos')}
                color={checkboxColor}
                colorScheme="blackAlpha"
                sx={{ '.chakra-checkbox__control': { borderColor: 'border.default' } }}
              >
                Activos
              </Checkbox>
              <Checkbox
                size="sm"
                isChecked={status === 'vencidos'}
                onChange={() => onCheckStatus('vencidos')}
                color={checkboxColor}
                colorScheme="blackAlpha"
                sx={{ '.chakra-checkbox__control': { borderColor: 'border.default' } }}
              >
                Pasados
              </Checkbox>
              <Checkbox
                size="sm"
                isChecked={status === 'todos'}
                onChange={() => onCheckStatus('todos')}
                color={checkboxColor}
                colorScheme="blackAlpha"
                sx={{ '.chakra-checkbox__control': { borderColor: 'border.default' } }}
              >
                Todos
              </Checkbox>
            </HStack>

            <Button
              size="sm"
              onClick={() => {
                setSortAsc((s) => !s);
                setCurrentPage(1);
              }}
              variant="outline"
              color="text.default"
              borderColor="border.default"
              leftIcon={
                sortAsc ? (
                  <ArrowUpIcon color="text.muted" />
                ) : (
                  <ArrowDownIcon color="text.muted" />
                )
              }
              title={
                sortAsc
                  ? 'Orden: fecha ascendente'
                  : 'Orden: fecha descendente'
              }
            >
              Fecha {sortAsc ? '↑' : '↓'}
            </Button>
          </HStack>
        </Flex>

        {/* Eventos especiales (paginados) */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          {pageEvents.map((event, idx) => {
            const eKey = buildEventKey(event);
            const highlighted = highlightedKey === eKey;
            const albumId = albumIdByEventKey.get(eKey);

            return (
              <Box
                key={`${event.title}-${event.date}-${idx}`}
                ref={setCardRef(eKey)}
                bg="bg.card"
                border="1px solid"
                borderColor={highlighted ? 'blue.400' : 'border.default'}
                borderRadius="xl"
                overflow="hidden"
                boxShadow={
                  highlighted
                    ? '0 0 0 3px rgba(59,130,246,0.45)'
                    : 'sm'
                }
                transition="box-shadow .3s ease, transform .25s ease, border-color .3s ease"
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: highlighted
                    ? '0 0 0 3px rgba(59,130,246,0.45)'
                    : 'md',
                }}
              >
                <Image
                  src={cld(event.image, {
                    w: 800,
                    h: 500,
                    crop: 'fill',
                    gravity: 'auto',
                  })}
                  alt={event.title}
                  w="100%"
                  h="240px"
                  objectFit="cover"
                  filter={isPast(event.date) ? 'grayscale(100%)' : 'none'}
                  fallbackSrc="https://via.placeholder.com/600x400?text=Imagen+no+cargada"
                />

                <VStack align="start" spacing={3} p={5}>
                  <Heading size="md" color="text.default" noOfLines={1}>
                    {event.title}
                  </Heading>

                  <Text color="text.muted" fontWeight="medium">
                    {isToday(event.date) && (
                      <Badge colorScheme="blue" mr={2}>
                        ¡Hoy!
                      </Badge>
                    )}
                    {event.date ? (
                      <>
                        {new Date(event.date).toLocaleDateString()}
                        {event.end_date &&
                          normalizeDate(event.end_date).getTime() !== normalizeDate(event.date).getTime() &&
                          ` - ${new Date(event.end_date).toLocaleDateString()}`}
                      </>
                    ) : (
                      ''
                    )}
                  </Text>

                  <HStack spacing={3} color="text.muted" fontSize="sm">
                    {event.time && (
                      <HStack spacing={1}>
                        <FiClock aria-hidden />
                        <Text>{event.time}</Text>
                      </HStack>
                    )}
                    {event.place && (
                      <HStack spacing={1}>
                        <FiMapPin aria-hidden />
                        <Text noOfLines={1}>{event.place}</Text>
                      </HStack>
                    )}
                  </HStack>

                  {event.description && (
                    <Text color="text.muted" noOfLines={3}>
                      {event.description}
                    </Text>
                  )}

                  {albumId && (
                    <Button
                      as={RouterLink}
                      to={`/albums?focusAlbumId=${encodeURIComponent(
                        albumId,
                      )}`}
                      colorScheme="blue"
                      size="sm"
                    >
                      Ver álbum
                    </Button>
                  )}
                </VStack>
              </Box>
            );
          })}
        </SimpleGrid>

        {finalSorted.length > PAGE_SIZE && (
          <Flex mt={10} justify="center" align="center" gap={4}>
            <Button
              onClick={() =>
                setCurrentPage((p) => Math.max(1, p - 1))
              }
              isDisabled={currentPage === 1}
              variant="outline"
              leftIcon={<ArrowLeftIcon color="text.muted" />}
              _hover={{ bg: 'whiteAlpha.200' }}
            />
            <Text color="text.muted">
              Página {currentPage} de {totalPages}
            </Text>
            <Button
              onClick={() =>
                setCurrentPage((p) => Math.min(totalPages, p + 1))
              }
              isDisabled={currentPage === totalPages}
              variant="outline"
              rightIcon={<ArrowRightIcon color="text.muted" />}
              _hover={{ bg: 'whiteAlpha.200' }}
            />
          </Flex>
        )}
      </Container>
    </Box>
  );
}
